import { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageShell, EmptyBox } from "@/components/PageShell";
import { OutboxItem } from "@/components/OutboxItem";
import { useOutboxPassword } from "@/components/OutboxPassword";

const LABEL = { x: "X", linkedin: "LinkedIn", whatsapp: "WhatsApp approval alerts" };

const ChannelChip = ({ name, on, paused }) => (
  <span data-testid={`outbox-channel-${name}`}
    className={`px-2.5 h-7 inline-flex items-center gap-1.5 border text-[10px] font-mono uppercase tracking-wider ${on ? "border-emerald-400/50 text-emerald-300" : "border-amber-400/40 text-amber-300"}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${on ? "bg-emerald-400" : "bg-amber-400"}`} />
    {LABEL[name]} · {on ? "connected" : paused ? "paused · keys saved" : "dry-run · keys missing"}
  </span>
);

export default function Outbox() {
  const [d, setD] = useState(null);
  const load = () => api.outbox().then(setD).catch(() => setD({ items: [], channels: {}, counts: {} }));
  useEffect(() => { load(); }, []);
  const update = (it) => setD((p) => ({ ...p, items: p.items.map((x) => (x.uid === it.uid ? it : x)) }));
  const [pwDialog, ensurePw] = useOutboxPassword();
  return (
    <PageShell testid="outbox-page" icon={Send} iconClass="text-atlas-cyan" title="Social Outbox"
      intro="Every confirmed call queues a share card for X and LinkedIn. Nothing posts until you approve it, and you get a WhatsApp ping each time a new card is waiting. While keys are missing, approved posts wait here and go out automatically once the keys are added.">
      {pwDialog}
      {d && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="outbox-channels">
          <ChannelChip name="x" on={d.channels.x} paused={d.social_paused} />
          <ChannelChip name="linkedin" on={d.channels.linkedin} paused={d.social_paused} />
          <ChannelChip name="whatsapp" on={d.channels.whatsapp} />
          <Link to="/manual-confirm" className="px-2.5 h-7 inline-flex items-center border border-white/15 text-[10px] font-mono uppercase tracking-wider text-atlas-secondary hover:text-white" data-testid="outbox-manual-confirm-link">Manual confirmation</Link>
          {d.social_paused && (
            <span className="text-[10px] font-mono uppercase tracking-widest text-amber-300 border border-amber-300/40 px-2 py-1" data-testid="outbox-paused">
              X & LinkedIn paused · approved posts wait here
            </span>
          )}
        </div>
      )}
      {d === null ? (
        <div className="text-[11px] font-mono text-atlas-secondary">Loading…</div>
      ) : d.items.length === 0 ? (
        <EmptyBox testid="outbox-empty" title="Outbox is empty." text="A post is drafted here the moment an Vigilo Earth call is confirmed." />
      ) : (
        <div className="flex flex-col gap-6" data-testid="outbox-list">
          {d.items.map((it) => <OutboxItem key={it.uid} it={it} xLimit={d.x_limit} onChange={update} paused={d.social_paused} ensurePw={ensurePw} />)}
        </div>
      )}
    </PageShell>
  );
}
